import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ButtonsComponent } from '../../../shared/buttons/buttons.component';
import { InputComponent } from '../../../shared/input/input.component';
import * as PopupConstants from '../../../shared/constants/popup-constants';
import { CommonModule } from '@angular/common';
import { SearchableMultiSelectComponent } from '../../../shared/searchable-multi-select/searchable-multi-select.component';
import { ShareItemsService } from '../../../services/share-feature/share-items.service';
import { forkJoin, lastValueFrom } from 'rxjs';
import { noWhitespaceValidator } from '../../../shared/validators/no-whitespace.validator';

@Component({
  selector: 'app-sample-edit-modal',
  standalone: true,
  imports: [
    InputComponent,
    ButtonsComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    CommonModule,
    SearchableMultiSelectComponent,
  ],
  templateUrl: './sample-edit-modal.component.html',
  styleUrl: './sample-edit-modal.component.css',
})
export class SampleEditModalComponent implements OnInit {
  sampleEditForm = new FormGroup({
    userName: new FormControl({ disabled: true, value: '' }),
    SampleName: new FormControl('', [Validators.required, Validators.maxLength(256), noWhitespaceValidator()]),
    SampleDescription: new FormControl('', [Validators.maxLength(1024), noWhitespaceValidator()]),
    groups: new FormControl<string[]>([]),
  });

  fullGroupsList: { GroupID: number; GroupName: string; IsPrimary: boolean }[] = [];

  groupOptions: string[] = [];
  initialSelectedGroups: string[] = [];
  disabledGroupOptions: string[] = [];
  primaryGroup: any = null;
  isSubmitting = false;

  //buttons
  buttonCancel = PopupConstants.BUTTON_CANCEL;
  arialLabel = PopupConstants.ARIAL_LABEL;
  btnMat = PopupConstants.BTN_MAT;
  btnStyleText = PopupConstants.BTN_STYLE_TEXT;

  buttonSave = PopupConstants.BUTTON_SAVE;
  btnMatFlat = PopupConstants.BTN_MAT_FLAT;
  btnStyleFilledSmall = PopupConstants.BTN_STYLE_FILLED_SMALL;
  btnStyleFilledSmallDisabled = PopupConstants.BTN_STYLE_FILLED_SMALL_DISABLED;

  constructor(
    public dialogRef: MatDialogRef<SampleEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public shareItemsService: ShareItemsService
  ) {
    if (data.sampleData.status === 'created') {
      this.sampleEditForm.get('AnalysisName')?.disable();
      this.sampleEditForm.get('AnalysisDescription')?.disable();
    }
    this.sampleEditForm.get('userName')?.setValue(data.sampleData.user ? data.sampleData.user : '');
    this.sampleEditForm.get('SampleName')?.setValue(data.sampleData.sampleName ? data.sampleData.sampleName : '');
    this.sampleEditForm
      .get('SampleDescription')
      ?.setValue(data.sampleData.sampleDescription ? data.sampleData.sampleDescription : '');
  }

  ngOnInit(): void {
    this.initializeGroupControl();
  }
  initializeGroupControl() {
    this.fullGroupsList = this.data.sampleData.fullGroups || [];
    this.primaryGroup = this.fullGroupsList.find((g: any) => g.IsPrimary);

    this.groupOptions = this.fullGroupsList.map((g) => g.GroupName);
    this.initialSelectedGroups = this.fullGroupsList.map((g) => g.GroupName);
    this.sampleEditForm.get('groups')?.setValue(this.initialSelectedGroups);

    if (this.primaryGroup) {
      this.disabledGroupOptions = [this.primaryGroup.GroupName];
    }
  }

  closePopup() {
    this.dialogRef.close({
      data: this.data.sampleData,
      event: 'CANCEL',
    });
  }

  getChangedFields(formGroup: FormGroup): any {
    const changedFields: any = {};
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control?.dirty) {
        changedFields[key] = control.value;
      }
    });
    return changedFields;
  }

  async onEditSubmit() {
    if (this.sampleEditForm.valid) {
      this.isSubmitting = true;
      const currentSelection = this.sampleEditForm.get('groups')?.value || [];
      const removedGroupNames = this.initialSelectedGroups.filter((name: string) => !currentSelection.includes(name));

      const apiCalls: any = [];
      const sampleId = this.data.sampleData.sampleId;

      // Process Removals (DELETE)
      if (removedGroupNames.length > 0) {
        const removedGroupIds = this.fullGroupsList
          .filter((g) => removedGroupNames.includes(g.GroupName))
          .map((g) => g.GroupID);

        removedGroupIds.forEach((groupId) => {
          apiCalls.push(this.shareItemsService.removeResourceFromGroup('samples', sampleId, groupId));
        });
      }
      if (apiCalls.length > 0) {
        try {
          await lastValueFrom(forkJoin(apiCalls));
        } catch (error) {
          console.error('Error removing groups', error);
        }
      }

      const updatedFields: any = {};
      const nameControl = this.sampleEditForm.get('SampleName');
      const descControl = this.sampleEditForm.get('SampleDescription');

      if (nameControl?.dirty) updatedFields['Name'] = nameControl.value;
      if (descControl?.dirty) updatedFields['Description'] = descControl.value;

      const hasMetadataChanges = Object.keys(updatedFields).length > 0;
      const hasGroupChanges = removedGroupNames.length > 0;

      if (hasMetadataChanges) {
        this.dialogRef.close({
          data: updatedFields,
          sampleId: this.data.sampleData.sampleId,
          event: 'SUBMIT',
        });
      } else if (hasGroupChanges) {
        this.dialogRef.close({
          event: 'RELOAD',
        });
      } else {
        this.closePopup();
      }
    }
  }
}
